import AmountsSummaryTooltip from '../../components/amount-summary-tooltip/amount-summary-tooltip';
import ExpenseItemFields from '../../components/expense-item-fields';
import {inject, observer} from "mobx-react";
import React, {useEffect, useMemo, useRef, useState} from "react";
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import toast from 'react-hot-toast';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faExclamationTriangle} from '@fortawesome/free-solid-svg-icons';


import ReceiptTotals, {expenseLineAmountLabel} from './receipt-totals';
import SharedReceiptNotice from './shared-receipt-notice';
import {buildSharedReceiptContext} from './utils/shared-receipt-context';
import {duplicateApprovalMessage} from '../../utils/expense-duplicate-message';

import InvoiceFormItem from "../../components/invoice-form-item";
import FormTextField from "../../components/form-textfield";
import FormAsyncSelect from "../../components/form-async-select";
import AutoCompleteField from "../../components/field-auto-complete";
import {expandMatterNumber, formatMatterLabelFromData, formatMatterNumber} from "../../utils/matter-display";
import Skeleton from "@mui/material/Skeleton";

import Modal from "../../components/modal_dialog";
import SupplierForm from "../../components/supplier-form";
import {fetch} from "../../utils/api-wrapper";
import { getCachedImageUrlByPath } from "../../utils/invoice-image-cache";
import PDFVisualizer from "../../components/pdf-visualizer";
import PdfImageDisplay from "../../components/pdf-image-display";
import {downloadFile} from "../../utils/file-manager";
import {buildAttachmentsListUrl, resolveAttachmentsCount} from "../../utils/attachments";
import {formatMoney} from "../../utils/format-money";
import Utils from "../../utils/utils";
import {ADERANT_LINE_REFERENCE_MAXLENGTH, ADERANT_HEADER_REFERENCE_MAXLENGTH} from "../../utils/aderant-field-limits";
import {resolveDefaultTaxCode} from "../../utils/tax-code-resolvers";
import {isEditorAdditionalFieldAllowed} from "../../utils/additional-fields-filter-helpers";
import {expenseUsesDisbursementCode} from "../review-expense/utils/contract-expense-json";
import TaxCodeSelector from "../../components/tax-code-selector";
import GlSegmentSelect from "../../components/gl-segment-select";
import Checkbox from "../../components/checkbox";
import "./invoice-approval-modal.scss";
import DeniedExpenseMoveModal from "./denied-expense-move-modal";

const APPROVAL_EDITABLE_CONTROL_CLASS = 'approval-modal__editable-control';

// Apply the visual input treatment only while a control can be edited.
const approvalControlClassName = (locked, classes = '') => [
    classes,
    locked ? '' : APPROVAL_EDITABLE_CONTROL_CLASS,
].filter(Boolean).join(' ');

const formatUserName = (user) => {
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    return fullName || user?.username || '';
};

const formatDate = (value) => {
    if (!value) return '';
    const isNumericTimestamp = typeof value === 'number' || /^\d+$/.test(String(value));
    const date = new Date(isNumericTimestamp ? Number(value) : value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
};

const translate = (data, key, fallback = key) => {
    const value = data?.t?.read?.(key);
    return typeof value === 'string' || typeof value === 'number' ? value : fallback;
};

const hasDisplayValue = (value) => value !== null && value !== undefined && value !== '';

const cloneValue = (value) => JSON.parse(JSON.stringify(value ?? {}));

const isBillableLineItem = (item) => item?.IsBillable === true || item?.IsBillable === 1;

// Coding routing reads the draft json, which the tax-calculation round trip may tag
// as `expense_invoice`, so the json marker counts alongside the invoice's own type.
const isExpenseTypeInvoice = (invoice = {}) => (
    invoice?.type === 'XPENSE'
    || invoice?.transaction_type === 'expense_invoice'
    || invoice?.json?.type === 'expense_invoice'
);

// XPENSE client lines keep using Matter/Cost Type even when IsBillable is false.
// Regular invoices continue to route directly from the line's IsBillable value.
const lineUsesDisbursementCoding = (invoice = {}, item = {}) => (
    isExpenseTypeInvoice(invoice)
        ? expenseUsesDisbursementCode(invoice.json ?? {}, item)
        : isBillableLineItem(item)
);

// The invoice-level flag represents whether every line is billable. A line approval
// can change one line without overwriting the status chosen for the other lines.
const deriveInvoiceIsBillable = (items = []) => (
    Array.isArray(items) && items.length > 0 && items.every(isBillableLineItem)
);

function BillableCheckbox({data, value, disabled, onChange}) {
    const label = translate(data, 'invoice.is_billable', 'Is billable');
    return (
        <Checkbox
            isChecked={Boolean(value)}
            disabled={disabled}
            onChange={onChange}
            inputProps={{'aria-label': label}}
        />
    );
}

const formatBoolean = (value) => {
    if (value === true || value === 1) return 'Yes';
    if (value === false || value === 0) return 'No';
    return '';
};

const formatObjectValue = (value) => {
    if (!value || typeof value !== 'object') return value;

    if (typeof value.label === 'string' || typeof value.label === 'number') {
        return value.label;
    }

    const code = value.code ?? value.Code ?? value.Id ?? value.value;
    const name = value.name ?? value.Description ?? value.description;
    if (code && name) return `${code} - ${name}`;
    if (code) return code;
    if (name) return name;

    const primitiveValues = Object.values(value).filter((entry) => (
        typeof entry === 'string' || typeof entry === 'number'
    ));
    return primitiveValues.join(' - ');
};

const formatLineItemValue = (value, format) => {
    if (format === 'date') return formatDate(value);
    if (format === 'money' || format === 'number') return formatMoney(value);
    if (format === 'boolean') return formatBoolean(value);
    return formatObjectValue(value);
};

const LINE_ITEM_COMMON_FIELDS = [
    {key: 'Description', labelKey: 'invoice.description', fallback: 'Description', fullWidth: true},
    {key: 'Office', labelKey: 'office', fallback: 'Office'},
    {key: 'GLUnit', labelKey: 'invoice.unit', fallback: 'Unit'},
    {key: 'GLOffice', labelKey: 'invoice.office', fallback: 'GL Office'},
    {key: 'WorkDate', labelKey: 'invoice.work_date', fallback: 'Work date', format: 'date'},
    {key: 'TaxCode', labelKey: 'invoice.tax_code', fallback: 'Tax code'},
    {key: 'Total', labelKey: 'approval_requests_table.amount', fallback: 'Amount', format: 'money'},
    {key: 'Status', labelKey: 'status', fallback: 'Status'},
];

// The two coding families are mutually exclusive on a line: a disbursement line is
// coded to a matter, a GL line to an account. Showing both would expose whichever
// one the line is not actually coded against.
const DISBURSEMENT_CODING_FIELDS = [
    {key: 'MatterId', labelKey: 'invoice.matter_id', fallback: 'Matter'},
    {key: 'CostType', labelKey: 'invoice.cost_type', fallback: 'Cost type'},
];

const GL_CODING_FIELDS = [
    {key: 'GLNatural', labelKey: 'invoice.gl_natural', fallback: 'GL Natural'},
    {key: 'GLDepartement', labelKey: 'invoice.gl_departement', fallback: 'GL Department'},
    {key: 'GLSection', labelKey: 'invoice.gl_section', fallback: 'GL Section'},
];

const BILLABLE_ADDITIONAL_FIELDS = [
    {key: 'WorkQuantity', labelKey: 'invoice.work_quantity', fallback: 'Work quantity', format: 'number'},
    {key: 'WorkRate', labelKey: 'invoice.work_rate', fallback: 'Work rate', format: 'money'},
    {key: 'WorkTotal', labelKey: 'invoice.work_total', fallback: 'Work total', format: 'money'},
    {key: 'Department', labelKey: 'invoice.department', fallback: 'Department'},
    {key: 'DepartmentId', labelKey: 'invoice.department', fallback: 'Department'},
    {key: 'GLPracticeGroup', labelKey: 'invoice.gl_practice_group', fallback: 'GL Practice Group'},
    {key: 'Activity', labelKey: 'invoice.activity', fallback: 'Activity'},
    {key: 'EmployeeId', labelKey: 'invoice.employee', fallback: 'Employee'},
    {key: 'IncurringEmployeeId', labelKey: 'invoice.incurring_employee', fallback: 'Incurring employee'},
    {key: 'AuthorizingEmployeeId', labelKey: 'invoice.authorizing_employee', fallback: 'Authorizing employee'},
    {key: 'JurisdictionId', labelKey: 'invoice.jurisdiction', fallback: 'Jurisdiction'},
    {key: 'HoldDate', labelKey: 'invoice.hold_date', fallback: 'Hold date', format: 'date'},
    {key: 'LocationId', labelKey: 'invoice.location', fallback: 'Location'},
    {key: 'ProjectId', labelKey: 'invoice.project', fallback: 'Project'},
    {key: 'Reference', labelKey: 'invoice.reference', fallback: 'Reference', maxLength: ADERANT_LINE_REFERENCE_MAXLENGTH},
    {key: 'ToBillAmount', labelKey: 'invoice.to_bill_amount', fallback: 'To-bill amount', format: 'money'},
    {key: 'CalculateAmountsAutomatically', labelKey: 'invoice.auto_calculation', fallback: 'Auto calculation', format: 'boolean'},
    {key: 'PreBillDisbursementNarrative', labelKey: 'invoice.pre_bill_narrative', fallback: 'Pre-bill narrative'},
    {key: 'Comments', labelKey: 'invoice.comments', fallback: 'Comments', fullWidth: true},
];

const NON_BILLABLE_ADDITIONAL_FIELDS = [
    {key: 'Department', labelKey: 'invoice.department', fallback: 'Department'},
    {key: 'DepartmentId', labelKey: 'invoice.department', fallback: 'Department'},
    {key: 'GLPracticeGroup', labelKey: 'invoice.gl_practice_group', fallback: 'GL Practice Group'},
    {key: 'Employee', labelKey: 'invoice.employee', fallback: 'Employee'},
    {key: 'EmployeeId', labelKey: 'invoice.employee', fallback: 'Employee'},
    {key: 'ProfitCenterId', labelKey: 'invoice.profit_center', fallback: 'Profit center'},
    {key: 'JurisdictionId', labelKey: 'invoice.jurisdiction', fallback: 'Jurisdiction'},
    {key: 'ProjectId', labelKey: 'invoice.project', fallback: 'Project'},
    {key: 'Comments', labelKey: 'invoice.comments', fallback: 'Comments', fullWidth: true},
];

// The detail panel honours the same tenant gates as the cost-details table and
// AdditionalFieldsModal, so a segment the client turned off stays hidden even when
// the line still carries a value for it.
const isApprovalAdditionalFieldAllowed = (data, field, tableKind) => {
    if (!['GLDepartement', 'GLSection', 'GLPracticeGroup'].includes(field.key)) return true;
    return isEditorAdditionalFieldAllowed(data, field.key, {
        tableKind,
        isLegalEngine: Utils.isEngineType(data, 'legal'),
    });
};

const buildLineItemFields = (data, invoice, item) => {
    const usesDisbursement = lineUsesDisbursementCoding(invoice, item);
    const tableKind = usesDisbursement ? 'billable' : 'gl';
    const codingFields = usesDisbursement ? DISBURSEMENT_CODING_FIELDS : GL_CODING_FIELDS;
    const additionalFields = usesDisbursement ? BILLABLE_ADDITIONAL_FIELDS : NON_BILLABLE_ADDITIONAL_FIELDS;
    const seen = new Set();
    return [...LINE_ITEM_COMMON_FIELDS, ...codingFields, ...additionalFields].filter((field) => {
        if (seen.has(field.key)) return false;
        seen.add(field.key);
        return isApprovalAdditionalFieldAllowed(data, field, tableKind);
    });
};

const isRenderableLineItemValue = (value) => {
    if (!hasDisplayValue(value)) return false;
    if (typeof value === 'object') {
        return hasDisplayValue(formatObjectValue(value));
    }
    return true;
};

const invoiceLineItemTableColumns = [
    {
        id: 'Description',
        labelKey: 'invoice.description',
        fallback: 'Description',
        minWidth: 180,
        format: 'data',
    },
    {
        id: 'MatterId',
        labelKey: 'invoice.matter_id',
        fallback: 'Matter ID',
        minWidth: 120,
        format: 'data',
    },
    {
        id: 'GLNatural',
        labelKey: 'invoice.gl_nat',
        fallback: 'GL Nat',
        minWidth: 120,
        format: 'data',
    },
    {
        id: 'GLDepartement',
        labelKey: 'invoice.gl_departement',
        fallback: 'GL Department',
        minWidth: 140,
        format: 'data',
    },
    {
        id: 'GLSection',
        labelKey: 'invoice.gl_section',
        fallback: 'GL Section',
        minWidth: 120,
        format: 'data',
    },
    {
        id: 'CostType',
        labelKey: 'invoice.cost_type',
        fallback: 'Type',
        minWidth: 100,
        format: 'data',
    },
    {
        id: 'TaxCode',
        labelKey: 'invoice.tax_code',
        fallback: 'Tax',
        minWidth: 90,
        format: 'data',
    },
    {
        id: 'amount',
        labelKey: 'invoice.amount',
        fallback: 'Amount',
        minWidth: 100,
        getValue: (row) => row.Total ?? row.WorkTotal ?? row.SubTotal,
        getUpdateKey: (row) => {
            if (hasDisplayValue(row.Total)) return 'Total';
            if (hasDisplayValue(row.WorkTotal)) return 'WorkTotal';
            return 'SubTotal';
        },
        format: 'money',
    },
];

const getInvoiceLineItemTableColumns = (data) => invoiceLineItemTableColumns.filter((column) => (
    !['GLDepartement', 'GLSection'].includes(column.id)
    || isEditorAdditionalFieldAllowed(data, column.id, {
        tableKind: 'gl',
        isLegalEngine: Utils.isEngineType(data, 'legal'),
    })
));

function ApprovalEditableField({
    value,
    format = 'data',
    locked,
    onChange,
    ariaLabel,
    classes = '',
    maxLength,
    searchURL = null,
    searchParams = null,
    searchOnFocus = false,
    searchCallback = null,
    passClearRecommendationFunc = null,
    hideAddOperation = false,
    noAddOperationHint = null,
}) {
    if (locked) {
        return <div className="fw-bold">{hasDisplayValue(value) ? formatLineItemValue(value, format) : '-'}</div>;
    }

    return (
        <FormTextField
            format={format}
            maxLength={maxLength}
            locked={false}
            value={value}
            validateChange={onChange}
            hideNA
            aria-label={ariaLabel}
            classes={approvalControlClassName(false, classes)}
            searchURL={searchURL}
            searchParams={searchParams}
            searchOnFocus={searchOnFocus}
            searchCallback={searchCallback}
            passClearRecommendationFunc={passClearRecommendationFunc}
            hideAddOperation={hideAddOperation}
            noAddOperationHint={noAddOperationHint}
        />
    );
}

function ApprovalLineItemInput({
    data,
    invoice = {},
    row = {},
    fieldKey,
    value,
    format = 'data',
    locked,
    onChange,
    ariaLabel,
    classes = '',
    maxLength,
    onEditExpense,
}) {
    const invoiceJson = invoice?.json || {};
    const isBillable = isBillableLineItem(row);
    const disabled = Boolean(locked);
    const controlClassName = approvalControlClassName(disabled, classes);
    const autocompleteClassName = `form-control ${approvalControlClassName(disabled, classes || 'border-0 pl-0')}`.trim();

    if (onEditExpense && fieldKey === 'Total' && !disabled) {
        return (
            <AmountsSummaryTooltip data={data} amountsSummary={{...row.amounts_summary}}
                onClick={() => onEditExpense(row)}>
                <FormTextField
                    format={format}
                    locked={true}
                    value={value}
                    hideNA
                    aria-label={ariaLabel}
                    classes={`${controlClassName} pointer`}
                />
            </AmountsSummaryTooltip>
        );
    }

    if (fieldKey === 'Description') {
        return (
            <FormTextField
                format='textarea-div'
                locked={disabled}
                value={value}
                validateChange={onChange}
                hideNA
                aria-label={ariaLabel}
                classes={approvalControlClassName(disabled, classes || 'border-0')}
            />
        );
    }

    if (fieldKey === 'WorkDate') {
        return (
            <ApprovalEditableField
                value={value || invoiceJson.WorkDate}
                format='date'
                locked={disabled}
                onChange={onChange}
                ariaLabel={ariaLabel}
                classes={classes}
            />
        );
    }

    if (fieldKey === 'MatterId') {
        return (
            <AutoCompleteField
                data={data}
                value={value ?? ''}
                onChange={(val) => onChange(val?.name ?? val ?? '')}
                className={autocompleteClassName}
                searchUrl='/matters/search/'
                // XPEN-3322: the picker edits the collapsed number; the full one is
                // restored before it is stored, so MatterId still resolves at export.
                formatEditValue={formatMatterNumber}
                parseEditValue={expandMatterNumber}
                minLength={1}
                disabled={disabled}
                placeholder={ariaLabel}
                // XPEN-3094 wired this mount to the tenant's matter_display
                // config; a later refactor of this renderer dropped it, so the
                // modal fell back to the default `<number> (<description>)`
                // label. Restored here (XPEN-3323) so the client-name prefix —
                // and the number/altNumber flags — apply in approvals too.
                formatLabel={(m) => formatMatterLabelFromData(data, m)}
            />
        );
    }

    if (fieldKey === 'CostType') {
        return (
            <AutoCompleteField
                value={value ?? ''}
                onChange={(val) => onChange(val?.name ?? val ?? '')}
                className={autocompleteClassName}
                searchUrl='/cost_type/search/'
                minLength={1}
                disabled={disabled}
                placeholder={ariaLabel}
            />
        );
    }

    if (fieldKey === 'GLNatural') {
        return (
            <AutoCompleteField
                value={value ?? ''}
                onChange={(val) => onChange(val?.name ?? val ?? '')}
                className={autocompleteClassName}
                searchUrl='/gl/nat/search/'
                minLength={1}
                disabled={disabled}
                placeholder={ariaLabel}
            />
        );
    }

    if (fieldKey === 'GLSection') {
        // Reuse the header/AdditionalFieldsModal picker: GL Section is a configured
        // client code persisted as {code, name}, not an unvalidated free-text string.
        return (
            <GlSegmentSelect
                segment='GLSection'
                value={value}
                onChange={onChange}
                isDisabled={disabled}
                name={`gl-section-select-${row?._view_id ?? 'line'}`}
                ariaLabel={ariaLabel}
                placeholder={ariaLabel}
            />
        );
    }

    if (fieldKey === 'TaxCode') {
        const displayTaxCode = value || invoiceJson?.TaxCode || resolveDefaultTaxCode(data.config?.tax_profile);
        return (
            // Reuse the transaction editor control so compact profiles cycle and larger profiles use a dropdown.
            <TaxCodeSelector
                taxCodes={data.config?.tax_profile?.map((taxCode) => taxCode.code) ?? []}
                value={displayTaxCode}
                disabled={disabled}
                onChange={onChange}
                className={classes}
            />
        );
    }

    if (fieldKey === 'Office' && Utils.isEngineType(data, "legal")) {
        return (
            <FormAsyncSelect
                searchUrl={"/offices/search"}
                placeholder={translate(data, 'office', 'Office')}
                value={value ?? null}
                onChange={(val) => onChange(val?.value ?? '')}
                isDisabled={disabled}
            />
        );
    }

    if (fieldKey === 'GLUnit' && !isBillable) {
        return (
            <FormAsyncSelect
                searchUrl={"/suppliers/unit/search"}
                placeholder={translate(data, 'invoice.unit', 'Unit')}
                value={value ?? null}
                optionMap={(option) => {
                    const name = option.name ?? "";
                    const description = option.description ?? "";
                    return {
                        value: { name, unit: option.unit },
                        label: option.label ?? `${name}${description ? ` (${description})` : ""}`,
                        name,
                        description,
                    };
                }}
                onChange={(val) => onChange(val?.value?.name ?? '')}
                isDisabled={disabled}
            />
        );
    }

    return (
        <ApprovalEditableField
            value={typeof value === 'object' ? formatObjectValue(value) : value}
            format={format}
            maxLength={maxLength}
            locked={disabled}
            onChange={onChange}
            ariaLabel={ariaLabel}
            classes={classes}
        />
    );
}

function InvoiceLineItemsTable({data, invoice, lineItems = [], locked, onLineItemChange, onEditExpense}) {
    if (!lineItems.length) return null;

    const columns = getInvoiceLineItemTableColumns(data);

    return (
        <>
            <Divider className={"my-4"} />
            <div>
                <h4 className="mb-3">{translate(data, 'invoice.cost_details', 'Cost Detail')}</h4>
                <Paper
                    elevation={0}
                    sx={{width: '100%', overflow: 'hidden', border: '1px solid #c1c7d0'}}
                >
                    <TableContainer sx={{maxHeight: 260}}>
                        <Table
                            stickyHeader
                            aria-label="invoice line items table"
                            sx={{
                                '& .MuiTableCell-root': {
                                    fontSize: '1rem',
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align}
                                            sx={{
                                                minWidth: column.minWidth,
                                                backgroundColor: '#f4f5f7',
                                                fontWeight: 600,
                                                fontSize: '1rem',
                                            }}
                                        >
                                            {column.id === 'amount' && invoice?.type === 'XPENSE'
                                                ? expenseLineAmountLabel(data) : translate(data, column.labelKey, column.fallback)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lineItems.map((row, index) => (
                                    <TableRow key={row._view_id || index}>
                                        {columns.map((column) => {
                                            const rawValue = column.getValue ? column.getValue(row) : row[column.id];
                                            const updateKey = column.getUpdateKey ? column.getUpdateKey(row) : column.id;
                                            const label = column.id === 'amount' && invoice?.type === 'XPENSE'
                                                ? expenseLineAmountLabel(data) : translate(data, column.labelKey, column.fallback);
                                            return (
                                                <TableCell key={column.id} align={column.align}>
                                                    <ApprovalLineItemInput
                                                        data={data}
                                                        onEditExpense={onEditExpense}
                                                        invoice={invoice}
                                                        row={row}
                                                        fieldKey={updateKey}
                                                        value={rawValue}
                                                        format={column.format}
                                                        locked={locked}
                                                        onChange={(value) => onLineItemChange(index, updateKey, value)}
                                                        ariaLabel={label}
                                                    />
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </div>
        </>
    );
}



function InvoiceApprovalModal({
    data,
    approvalRequest,
    onClose,
    onInvoiceUpdated = null,
    allowApprovalActions = true,
    allowResolvedEditing = false,
    allowDeleteAction = false,
    allowResendAction = false,
    allowMoveExpenseAction = false,
}) {
    const [invoice, setInvoice] = useState(null);
    const [draftInvoiceJson, setDraftInvoiceJson] = useState({});
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loadingInvoice, setLoadingInvoice] = useState(false);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [invoiceError, setInvoiceError] = useState(false);
    const [pdfError, setPdfError] = useState(false);
    const [attachmentsCount, setAttachmentsCount] = useState(0);
    const [pdfView, setPdfView] = useState("invoice");
    const attachmentsRequestGenerationRef = useRef(0);
    const pdfRequestGenerationRef = useRef(0);
    const [processingAction, setProcessingAction] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [showDenialModal, setShowDenialModal] = useState(false);
    const [causeDenial, setCauseDenial] = useState('');
    const [denialError, setDenialError] = useState(null);
    const [savingInvoice, setSavingInvoice] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [taxCalculationInvoiceId, setTaxCalculationInvoiceId] = useState(null);
    const [taxError, setTaxError] = useState(false);
    const [expenseItem, setExpenseItem] = useState(null);
    // Serialized copy of the expense item as it was opened, for the dirty check.
    const pristineExpenseItem = useRef(null);
    const calculatedDraft = useRef(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [deletingRequest, setDeletingRequest] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [resendingRequest, setResendingRequest] = useState(false);
    const [resendError, setResendError] = useState(null);
    const [showMoveExpenseModal, setShowMoveExpenseModal] = useState(false);
    const [promptNewSupplier, setPromptNewSupplier] = useState(false);
    const [newSupplierName, setNewSupplierName] = useState('');
    const [currentSupplier, setCurrentSupplier] = useState(null);
    const [askSupplierCodesAvailable, setAskSupplierCodesAvailable] = useState(null);
    const [chosenSupplierNumber, setChosenSupplierNumber] = useState(null);
    // XPEN-2861: the expense policy issues raised against the expense behind this
    // request. Evaluated server-side against the saved source expenses, so the
    // refresh key re-asks after the approver's edits are persisted.
    const [policyIssues, setPolicyIssues] = useState([]);
    const [policyError, setPolicyError] = useState(false);
    const [policyRefreshKey, setPolicyRefreshKey] = useState(0);
    // FormTextField hands back a function that clears its open proposition list,
    // so a supplier picked (or created) here does not leave a stale dropdown.
    const clearSupplierRecommendation = useRef(null);

    const invoiceJson = invoice?.json || {};
    const draftJson = draftInvoiceJson || {};
    const taxesChanged = taxCalculationInvoiceId === approvalRequest?.invoice_id;
    const calculatingTaxes = taxesChanged && calculatedDraft.current !== draftInvoiceJson && !taxError;
    const taxesBlocked = calculatingTaxes || taxError;

    // Recalculate subsequent draft edits too: an in-flight response must never
    // overwrite newer inputs. The returned draft skips the effect's next pass.
    useEffect(() => {
        if (!taxesChanged || calculatedDraft.current === draftInvoiceJson) return;
        let current = true;
        setTaxError(false);
        const calculationJson = cloneValue(draftInvoiceJson);
        if (invoice?.transaction_type === 'expense_invoice' || invoice?.type === 'XPENSE') calculationJson.type = 'expense_invoice';
        fetch('/utils/compute_taxes', 'POST', calculationJson, response => {
            if (!current) return;
            if (draftInvoiceJson.type === undefined) delete response.data.json.type;
            else response.data.json.type = draftInvoiceJson.type;
            calculatedDraft.current = response.data.json;
            setDraftInvoiceJson(response.data.json);
        }, () => {
            if (current) setTaxError(true);
        });
        return () => { current = false; };
    }, [draftInvoiceJson, taxesChanged, invoice?.transaction_type, invoice?.type]);
    // draftJson starts as cloneValue(invoice.json) and every updater rebuilds it
    // through cloneValue, so key order matches and stringify comparison is a
    // reliable dirty check. Used to skip the implicit save on approval when the
    // approver changed nothing, keeping that path at a single request.
    const hasUnsavedChanges = useMemo(
        () => JSON.stringify(draftInvoiceJson || {}) !== JSON.stringify(invoice?.json || {}),
        [draftInvoiceJson, invoice]
    );
    const lineItem = useMemo(() => {
        if (approvalRequest?.type !== 'line_item') return null;
        const items = draftJson.ListItem || [];
        const item = items.find((candidate) => String(candidate._view_id) === String(approvalRequest.line_item_view_id));
        return item ? {
            ...item,
            _mileage_origin: item._mileage_origin || approvalRequest.line_item?._mileage_origin,
            _mileage_destination: item._mileage_destination || approvalRequest.line_item?._mileage_destination,
        } : approvalRequest.line_item;
    }, [approvalRequest, draftJson.ListItem]);
    const displayedLineItem = useMemo(() => {
        if (approvalRequest?.type !== 'line_item') return null;
        const items = invoiceJson.ListItem || [];
        const item = items.find((candidate) => String(candidate._view_id) === String(approvalRequest.line_item_view_id));
        return item ? {
            ...item,
            _mileage_origin: item._mileage_origin || approvalRequest.line_item?._mileage_origin,
            _mileage_destination: item._mileage_destination || approvalRequest.line_item?._mileage_destination,
        } : approvalRequest.line_item;
    }, [approvalRequest, invoiceJson.ListItem]);
    // Derived, not fetched: the invoice already holds every sibling line from a
    // receipt shared between matters, keyed by a shared `checksum` (XPEN-2935).
    const sharedReceiptContext = useMemo(() => {
        if (approvalRequest?.type !== 'line_item') return null;
        return buildSharedReceiptContext(invoiceJson, approvalRequest.line_item_view_id);
    }, [approvalRequest, invoiceJson.ListItem]);

    useEffect(() => {
        if (!approvalRequest?.invoice_id) return;

        setExpenseItem(null);
        setTaxCalculationInvoiceId(null);
        setTaxError(false);
        calculatedDraft.current = null;
        setLoadingInvoice(true);
        setInvoiceError(false);
        fetch(`/invoices/${approvalRequest.invoice_id}`, "GET", {}, response => {
            const loadedInvoice = response.data.invoice;
            setInvoice(loadedInvoice);
            setDraftInvoiceJson(cloneValue(loadedInvoice?.json));
            setLoadingInvoice(false);
        }, () => {
            setInvoiceError(true);
            setLoadingInvoice(false);
        });
    }, [approvalRequest?.invoice_id]);

    // XPEN-2861: only an XPENSE invoice tied to an expense account has policies to
    // evaluate. An invoice approval covers every receipt on the report; a line item
    // approval is narrowed to that line's expense by the endpoint's checksum filter.
    useEffect(() => {
        setPolicyIssues([]);
        setPolicyError(false);

        if (invoice?.type !== 'XPENSE' || !invoice?.id_expense_account) return;

        const policyPath = approvalRequest?.type === 'line_item'
            ? `/approvals/expense-policy-issues/line-item/${approvalRequest.invoice_id}/${approvalRequest.line_item_view_id}`
            : `/approvals/expense-policy-issues/invoice/${approvalRequest.invoice_id}`;
        // Approve→next keeps this modal mounted and swaps the request, so a slow
        // response for the previous request must not land on the current one.
        let isCurrentRequest = true;

        fetch(policyPath, "GET", {}, response => {
            if (isCurrentRequest) setPolicyIssues(response?.data?.issues ?? []);
        }, () => {
            if (isCurrentRequest) setPolicyError(true);
        });

        return () => {
            isCurrentRequest = false;
        };
    }, [
        invoice?.type,
        invoice?.id_expense_account,
        approvalRequest?.invoice_id,
        approvalRequest?.line_item_view_id,
        approvalRequest?.type,
        policyRefreshKey,
    ]);

    useEffect(() => {
        if (!approvalRequest?.file_name) return;

        pdfRequestGenerationRef.current += 1;
        const requestedGeneration = pdfRequestGenerationRef.current;
        setPdfUrl(null);
        setLoadingPdf(true);
        setPdfError(false);
        const pdfPath = approvalRequest.type === 'line_item'
            ? `/approvals/line-item/${approvalRequest.invoice_id}/${approvalRequest.line_item_view_id}/pdf`
            : `/invoice/pdf/${approvalRequest.file_name}`;
        const cacheKey = approvalRequest.type === 'line_item'
            ? `line-item:${approvalRequest.invoice_id}:${approvalRequest.line_item_view_id}`
            : approvalRequest.file_name;
        getCachedImageUrlByPath(cacheKey, pdfPath)
            .then(url => {
                if (requestedGeneration !== pdfRequestGenerationRef.current) return;
                setPdfUrl(url || null);
                setPdfError(!url);
                setLoadingPdf(false);
            })
            .catch(() => {
                if (requestedGeneration !== pdfRequestGenerationRef.current) return;
                setPdfUrl(null);
                setPdfError(true);
                setLoadingPdf(false);
            });
    }, [approvalRequest?.file_name, approvalRequest?.invoice_id, approvalRequest?.line_item_view_id, approvalRequest?.type]);

    useEffect(() => {
        attachmentsRequestGenerationRef.current += 1;
        const requestedGeneration = attachmentsRequestGenerationRef.current;
        setAttachmentsCount(0);
        setPdfView("invoice");

        if (data.isMobile || !approvalRequest?.invoice_id) return;

        fetch(
            buildAttachmentsListUrl('invoice', approvalRequest.invoice_id),
            'GET',
            {},
            (response) => {
                if (requestedGeneration !== attachmentsRequestGenerationRef.current) return;
                setAttachmentsCount(resolveAttachmentsCount(response));
            },
            () => {
                if (requestedGeneration !== attachmentsRequestGenerationRef.current) return;
                setAttachmentsCount(0);
            },
        );
    }, [approvalRequest?.invoice_id, data.isMobile]);

    if (!approvalRequest) return null;

    // XPEN-3200: who sent this for approval. The backend resolves
    // json.approval_requested_by and already falls back to the invoice owner;
    // the summary row's requester covers the window before the invoice loads.
    const approvalRequesterName = formatUserName(invoice?.approval_requester)
        || formatUserName(approvalRequest.requester)
        || formatUserName(invoice?.user);

    const renderApprovalRequesterField = () => (
        <InvoiceFormItem
            alwaysUseMobileStyling={true}
            labelClassName="text-muted align-self-start"
            data={data}
            label={translate(data, 'approval_requests_table.requester', 'Requester')}
            show={true}
        >
            <div className="fw-bold">{approvalRequesterName || '-'}</div>
        </InvoiceFormItem>
    );

    const isResolvedRequest = Boolean(approvalRequest.resolved_action);
    const title = approvalRequest.type === 'line_item'
        ? translate(data, 'approval_modal.line_item_title', 'Line item approval')
        : translate(data, 'approval_modal.invoice_title', 'Invoice approval');
    const resolvedStatus = isResolvedRequest
        ? translate(data, `status.${approvalRequest.resolved_action}`, approvalRequest.resolved_action)
        : null;
    const resolvedStatusClassName = approvalRequest.resolved_action === 'approved'
        ? 'text-success'
        : approvalRequest.resolved_action === 'denied' ? 'text-danger' : '';
    const denialReason = approvalRequest.resolved_action === 'denied'
        ? String(approvalRequest.reason || '').trim()
        : '';
    const deletesAssociatedExpense = Boolean(
        approvalRequest.type === 'line_item'
        && invoice?.type === 'XPENSE'
        && invoice?.id_expense_account
        && lineItem?.checksum
    );
    const canMoveDeniedExpense = Boolean(
        allowMoveExpenseAction
        && approvalRequest.resolved_action === 'denied'
        && approvalRequest.type === 'line_item'
        && invoice?.type === 'XPENSE'
        && invoice?.id_expense_account
        && lineItem?.checksum
        && approvalRequest.id
    );

    const openFullInvoice = () => {
        window.open(`/dashboard?invoice_id=${approvalRequest.invoice_id}`, '_blank', 'noopener,noreferrer');
    };

    const getApprovalActionUrl = (action) => {
        if (approvalRequest.type === 'line_item') {
            return `/approvals/line-item/${approvalRequest.invoice_id}/${approvalRequest.line_item_view_id}/${action}`;
        }

        return `/approvals/invoice/${approvalRequest.invoice_id}/${action}`;
    };

    const submitApprovalAction = (action, cause = null) => {
        if (processingAction || (action === 'approve' && (taxesBlocked || expenseItem))) return;

        setProcessingAction(action);
        setActionError(null);

        // XPEN-3148: approval is the final step, so saving is implicit in it — no
        // separate "Save changes" step for the approver. The approval endpoints
        // re-read the invoice from the database (and ignore any client-supplied
        // JSON, per XPEN-3135), so the draft has to be persisted first and the
        // action only fired once that write succeeded. A failed save aborts the
        // approval and leaves the modal open with the draft intact. Denial keeps
        // its previous behaviour and discards unsaved edits.
        if (action === 'approve' && canEditInvoice && hasUnsavedChanges) {
            persistDraftInvoice({
                notify: false,
                refreshPolicies: false,
                // Nothing runs on failure: persistDraftInvoice already surfaced the
                // error, and `processingAction` stays set so the approval is not
                // retried against a draft known not to have been persisted.
                onSuccess: () => postApprovalAction(action, cause),
            });
            return;
        }

        postApprovalAction(action, cause);
    };

    const postApprovalAction = (action, cause = null) => {
        const body = cause ? {cause} : {};
        fetch(getApprovalActionUrl(action), "POST", body, () => {
            setProcessingAction(null);
            toast.success(translate(data, `approval_modal.${action}_success`, 'Approval action completed.'));
            onClose(true, approvalRequest);
        }, (response) => {
            setProcessingAction(null);
            const errorMessage = duplicateApprovalMessage(data, response?.response?.data, 'approver')
                || translate(data, 'approval_modal.action_error', 'Approval action could not be completed.');
            setActionError(errorMessage);
            toast.error(errorMessage);
        });
    };

    const openDenialModal = () => {
        setCauseDenial('');
        setActionError(null);
        setDenialError(null);
        setShowDenialModal(true);
    };

    const confirmDenial = () => {
        const trimmed = causeDenial.trim();
        if (!trimmed) {
            setDenialError(translate(data, 'approval_modal.deny_reason_required', 'A reason is required to deny this request.'));
            return false;
        }

        setShowDenialModal(false);
        submitApprovalAction('deny', trimmed);
    };

    const actionInProgress = processingAction !== null;
    const canEditInvoice = (
        (!isResolvedRequest || allowResolvedEditing)
        && invoice
        && !loadingInvoice
    );
    // XPEN-3148: an approver never saves as a separate step — Approve saves for
    // them. The button stays only where no approval action is offered: the
    // requester's own pending/denied request (allowApprovalActions=false) and
    // resolved-request editing, where saving is the only way to persist an edit.
    const showSaveButton = canEditInvoice && !(allowApprovalActions && !isResolvedRequest);
    const updateDraftField = (key, value) => {
        setSaveError(null);
        setDraftInvoiceJson((current) => ({
            ...cloneValue(current),
            [key]: value,
        }));
    };
    const updateDraftSupplierField = (key, value) => {
        setSaveError(null);
        setDraftInvoiceJson((current) => {
            const next = cloneValue(current);
            const suppliers = Array.isArray(next.ListSupplier) ? [...next.ListSupplier] : [{}];
            suppliers[0] = {...(suppliers[0] || {}), [key]: value};
            next.ListSupplier = suppliers;
            return next;
        });
    };
    const updateDraftSupplierFields = (fields) => {
        setSaveError(null);
        setDraftInvoiceJson((current) => {
            const next = cloneValue(current);
            const suppliers = Array.isArray(next.ListSupplier) ? [...next.ListSupplier] : [{}];
            suppliers[0] = {...(suppliers[0] || {}), ...fields};
            next.ListSupplier = suppliers;
            return next;
        });
    };
    // Mirrors the invoice editor's supplierChosen (components/invoice.js): a picked
    // supplier resolves its accounting code through /suppliers/code/{id}, an
    // ambiguous supplier asks the user which code to use, and "add it" opens the
    // shared SupplierForm. Supplier-driven coding defaults (GL, cost type, office,
    // unit, due date, line item recoding) are deliberately NOT reapplied here —
    // the approver's existing coding is what is under review.
    const supplierChosen = (supplier, typedName = null) => {
        if (!supplier) {
            // XPEN-3267 — the supplier field hides "add it" for tenants that may
            // not create suppliers; this guards any other route to a null pick.
            if (!Utils.clientAllowsSupplierCreate(data)) {
                setPromptNewSupplier(false);
                return;
            }
            setNewSupplierName(typedName || '');
            setPromptNewSupplier(true);
            return;
        }

        const supplierName = supplier?.names?.[0] ?? supplier?.name ?? '';
        setPromptNewSupplier(false);

        fetch(`/suppliers/code/${supplier._id}`, "GET", null, (response) => {
            if (clearSupplierRecommendation.current) clearSupplierRecommendation.current();

            if (response?.data?.code_found != null) {
                updateDraftSupplierFields({
                    Name: supplierName,
                    Number: response.data.code_found,
                    Index: supplier._id,
                });
                return;
            }

            // No single code: keep the name change and let the user pick a code.
            updateDraftSupplierFields({Name: supplierName, Index: supplier._id});
            if (response?.data?.codes_available?.length > 0) {
                setCurrentSupplier(supplier);
                setChosenSupplierNumber(null);
                setAskSupplierCodesAvailable(response.data.codes_available);
            }
        }, () => {
            if (clearSupplierRecommendation.current) clearSupplierRecommendation.current();
            updateDraftSupplierFields({Name: supplierName, Index: supplier._id});
        });
    };
    const confirmSupplierNumber = () => {
        if (chosenSupplierNumber == null) return false;

        const formData = new FormData();
        formData.append('id_supplier', currentSupplier._id);
        formData.append('code', chosenSupplierNumber);
        fetch('/supplier-codes', "POST", formData, () => {
            updateDraftSupplierFields({Number: chosenSupplierNumber});
            setChosenSupplierNumber(null);
            setAskSupplierCodesAvailable(null);
        });

        return true;
    };
    const isExpenseInvoice = invoice?.type === 'XPENSE' || invoice?.transaction_type === 'expense_invoice';
    const openExpenseItem = isExpenseInvoice ? (item) => {
        const opened = {...cloneValue(item), amounts_summary: {
            ...item.amounts_summary,
            ItemSubTotal: item.amounts_summary?.ItemSubTotal ?? item.SubTotal ?? item.Total ?? 0,
            TipAllocatedToItem: item.amounts_summary?.TipAllocatedToItem ?? item.TipAllocated ?? 0,
        }};

        pristineExpenseItem.current = JSON.stringify(opened);
        setExpenseItem(opened);
    } : undefined;
    const applyExpenseItem = () => {
        const subtotal = Number(expenseItem.amounts_summary.ItemSubTotal);
        const tip = Number(expenseItem.amounts_summary.TipAllocatedToItem);
        const updated = {...expenseItem, SubTotal: subtotal, TipAllocated: tip, Total: subtotal + tip};
        setDraftInvoiceJson((current) => {
            const items = current.ListItem.map(item => String(item._view_id) === String(updated._view_id) ? updated : item);
            return {...current, ListItem: items, Total: items.reduce((total, item) => total + Number(item.Total || 0), 0)};
        });
        setTaxError(false);
        setTaxCalculationInvoiceId(approvalRequest.invoice_id);
        setExpenseItem(null);
    };

    const updateDraftLineItemField = (index, key, value) => {
        if (key === 'TaxCode') {
            setTaxCalculationInvoiceId(approvalRequest.invoice_id);
            setTaxError(false);
        }
        setSaveError(null);
        setDraftInvoiceJson((current) => {
            const next = cloneValue(current);
            const items = Array.isArray(next.ListItem) ? [...next.ListItem] : [];
            items[index] = {...(items[index] || {}), [key]: value};
            next.ListItem = items;
            return next;
        });
    };
    const updateDraftSelectedLineItemField = (key, value) => {
        if (key === 'TaxCode') {
            setTaxCalculationInvoiceId(approvalRequest.invoice_id);
            setTaxError(false);
        }
        setSaveError(null);
        setDraftInvoiceJson((current) => {
            const next = cloneValue(current);
            const items = Array.isArray(next.ListItem) ? [...next.ListItem] : [];
            const index = items.findIndex((item) => String(item._view_id) === String(approvalRequest.line_item_view_id));
            if (index >= 0) {
                items[index] = {...items[index], [key]: value};
                next.ListItem = items;
                if (key === 'IsBillable') {
                    next.IsBillable = deriveInvoiceIsBillable(items);
                }
            }
            return next;
        });
    };
    const updateDraftInvoiceBillable = (value) => {
        setSaveError(null);
        setDraftInvoiceJson((current) => {
            const next = cloneValue(current);
            next.IsBillable = value;
            // Match the expense editor: changing the header applies the same status to every line.
            next.ListItem = (Array.isArray(next.ListItem) ? next.ListItem : []).map((item) => ({
                ...item,
                IsBillable: value ? 1 : 0,
            }));
            return next;
        });
    };
    // Persists the pending draft. `status` is deliberately the pre-action status:
    // this must run strictly BEFORE an approval POST, never after, or it would
    // revert the status the approval endpoint just wrote.
    // Returns whether the save was actually started, so a caller sequencing another
    // request behind it can stand down instead of waiting on a callback that never runs.
    const persistDraftInvoice = ({onSuccess = null, onFailure = null, notify = true, refreshPolicies = true} = {}) => {
        if (!invoice || savingInvoice || taxesBlocked || expenseItem) return false;

        setSavingInvoice(true);
        setSaveError(null);
        const formData = new FormData();
        formData.append('json', JSON.stringify(draftJson));
        formData.append('status', invoice.status || invoiceJson.Status || 'Need_Approval');
        formData.append('status_details', invoice.status_details || '');

        fetch(`/invoices/${approvalRequest.invoice_id}`, "PUT", formData, () => {
            const savedJson = cloneValue(draftJson);
            calculatedDraft.current = savedJson;
            setInvoice((current) => ({...(current || {}), json: savedJson}));
            setDraftInvoiceJson(savedJson);
            // XPEN-2861: the saved edit may raise or clear a policy issue, so re-ask
            // wherever the approver stays on this request. Not on the approve path:
            // that save is immediately followed by the action and the modal closing.
            if (refreshPolicies) setPolicyRefreshKey((current) => current + 1);
            setSavingInvoice(false);
            if (onInvoiceUpdated) onInvoiceUpdated();
            if (notify) toast.success(translate(data, 'approval_modal.save_success', 'Invoice changes saved.'));
            if (onSuccess) onSuccess();
        }, () => {
            const errorMessage = translate(data, 'approval_modal.save_error', 'Invoice changes could not be saved.');
            setSaveError(errorMessage);
            setSavingInvoice(false);
            toast.error(errorMessage);
            if (onFailure) onFailure();
        });

        return true;
    };

    const saveInvoiceChanges = () => persistDraftInvoice();

    const finishDeleteSuccess = () => {
        setDeletingRequest(false);
        setShowDeleteConfirmation(false);
        if (onInvoiceUpdated) onInvoiceUpdated();
        toast.success(translate(data, 'approval_modal.delete_success', 'Approval item deleted.'));
        onClose(false);
    };

    const finishExpenseMove = () => {
        setShowMoveExpenseModal(false);
        if (onInvoiceUpdated) onInvoiceUpdated();
        toast.success(translate(data, 'approval_modal.move_expense_success', 'Expense moved.'));
        onClose(false);
    };

    const failDelete = () => {
        const errorMessage = translate(
            data,
            'approval_modal.delete_error',
            'Approval item could not be deleted.'
        );
        setDeletingRequest(false);
        setDeleteError(errorMessage);
        toast.error(errorMessage);
    };

    const persistDeletedLineItems = (nextJson, rebuildMergedExpense = false) => {
        const formData = new FormData();
        formData.append('json', JSON.stringify(nextJson));
        formData.append('status', invoice.status || invoiceJson.Status || 'Denied');
        formData.append('status_details', invoice.status_details || '');
        fetch(
            `/invoices/${approvalRequest.invoice_id}`,
            "PUT",
            formData,
            () => {
                if (!rebuildMergedExpense) {
                    finishDeleteSuccess();
                    return;
                }
                fetch(
                    `/invoices/${approvalRequest.invoice_id}/expenses/rebuild`,
                    "POST",
                    {},
                    finishDeleteSuccess,
                    failDelete
                );
            },
            failDelete
        );
    };

    const confirmDeleteRequest = () => {
        if (!invoice || deletingRequest) return false;

        setDeletingRequest(true);
        setDeleteError(null);

        if (approvalRequest.type === 'line_item') {
            const nextJson = cloneValue(invoiceJson);
            const currentItems = Array.isArray(nextJson.ListItem) ? nextJson.ListItem : [];
            const targetItem = currentItems.find(
                (item) => String(item._view_id) === String(approvalRequest.line_item_view_id)
            );
            if (!targetItem) {
                failDelete();
                return false;
            }

            const expenseAccountId = invoice.id_expense_account;
            const expenseChecksum = targetItem.checksum;
            const comesFromExpenseAccount = (
                invoice.type === 'XPENSE'
                && expenseAccountId
                && expenseChecksum
            );

            nextJson.ListItem = currentItems.filter((item) => (
                comesFromExpenseAccount
                    ? String(item.checksum) !== String(expenseChecksum)
                    : String(item._view_id) !== String(approvalRequest.line_item_view_id)
            ));

            if (comesFromExpenseAccount) {
                fetch(
                    `/expenses_account/expenses/${expenseAccountId}`,
                    "DELETE",
                    {checksums: [expenseChecksum]},
                    () => persistDeletedLineItems(nextJson, true),
                    failDelete
                );
                return false;
            }

            persistDeletedLineItems(nextJson);
            return false;
        }

        fetch(
            `/invoices/${approvalRequest.invoice_id}`,
            "DELETE",
            {},
            finishDeleteSuccess,
            failDelete
        );
        return false;
    };

    const sendResendRequest = () => {
        fetch(
            '/approvals/denied/resend',
            "POST",
            {request_id: approvalRequest.id},
            () => {
                setResendingRequest(false);
                if (onInvoiceUpdated) onInvoiceUpdated();
                toast.success(translate(
                    data,
                    'approval_modal.resend_success',
                    'Approval request resent.'
                ));
                onClose(false);
            },
            (response) => {
                setResendingRequest(false);
                const errorMessage = duplicateApprovalMessage(data, response?.response?.data, 'requester')
                    || translate(
                        data,
                        'approval_modal.resend_error',
                        'Approval request could not be resent.'
                    );
                setResendError(errorMessage);
                toast.error(errorMessage);
            }
        );
    };

    const resendApprovalRequest = () => {
        if (resendingRequest || !approvalRequest.id) return;

        setResendingRequest(true);
        setResendError(null);

        // A resend restarts the approval from what is stored, and the endpoint
        // re-reads the invoice from the database (XPEN-3135), so pending edits have
        // to be persisted first or they never reach the approver it goes back to.
        // The resend only fires from the successful save callback.
        if (canEditInvoice && hasUnsavedChanges) {
            const saveStarted = persistDraftInvoice({
                notify: false,
                onSuccess: sendResendRequest,
                // persistDraftInvoice already surfaced the save error; just drop the
                // resend spinner so the approver can fix the draft and try again.
                onFailure: () => setResendingRequest(false),
            });
            if (!saveStarted) setResendingRequest(false);
            return;
        }

        sendResendRequest();
    };

    const denyText = translate(data, 'deny', translate(data, 'disapprove', 'Deny'));
    // A failed implicit save keeps `processingAction` set (the approval must not
    // run against an unpersisted draft), so drop the spinner once the sequence
    // has stopped waiting on anything.
    const approving = processingAction === 'approve' && !saveError;
    const approveText = translate(data, 'approve', 'Approve');
    const saveText = translate(data, 'approval_modal.save_changes', 'Save changes');
    const resendText = translate(data, 'approval_modal.resend_request', 'Resend approval request');
    const requestAlreadyPending = approvalRequest.type === 'line_item'
        ? lineItem?.Status === 'Need_Approval'
        : invoice?.status === 'Need_Approval' || invoiceJson.Status === 'Need_Approval';
    // The approver's real primary action carries the footer: approving an open
    // request, or resending one that came back denied. Everything else is a
    // quieter secondary, with deny and delete marked destructive.
    const canApprove = !isResolvedRequest && allowApprovalActions;
    const canResend = allowResendAction
        && approvalRequest.resolved_action === 'denied'
        && !requestAlreadyPending;

    const primaryFooterAction = canApprove
        ? {
            text: approveText,
            action: () => submitApprovalAction('approve'),
            loading: approving,
            disabled: actionInProgress || savingInvoice || taxesBlocked || Boolean(expenseItem),
        }
        : canResend
            ? {
                text: resendText,
                action: resendApprovalRequest,
                loading: resendingRequest,
                disabled: actionInProgress || savingInvoice || deletingRequest || resendingRequest || taxesBlocked || Boolean(expenseItem),
            }
            : null;

    const secondaryFooterActions = [
        {
            text: translate(data, 'approval_modal.open_full_invoice', 'Open full invoice'),
            action: openFullInvoice,
            disabled: actionInProgress || savingInvoice || resendingRequest,
            noAutoDisable: true,
        },
        ...(showSaveButton ? [{
            text: saveText,
            action: saveInvoiceChanges,
            loading: savingInvoice,
            disabled: actionInProgress || savingInvoice || resendingRequest || taxesBlocked || Boolean(expenseItem),
            noAutoDisable: true,
        }] : []),
        // Resend only appears here when approving is available and took the primary slot.
        ...(canResend && canApprove ? [{
            text: resendText,
            action: resendApprovalRequest,
            loading: resendingRequest,
            disabled: actionInProgress || savingInvoice || deletingRequest || resendingRequest || taxesBlocked || Boolean(expenseItem),
            noAutoDisable: true,
        }] : []),
        ...(canMoveDeniedExpense ? [{
            text: translate(data, 'approval_modal.move_expense', 'Move expense'),
            action: () => setShowMoveExpenseModal(true),
            disabled: actionInProgress || savingInvoice || deletingRequest || resendingRequest,
            noAutoDisable: true,
        }] : []),
        ...(allowDeleteAction && approvalRequest.resolved_action === 'denied' ? [{
            text: translate(data, 'delete', 'Delete'),
            action: () => {
                setDeleteError(null);
                setShowDeleteConfirmation(true);
            },
            destructive: true,
            loading: deletingRequest,
            disabled: actionInProgress || savingInvoice || deletingRequest || resendingRequest,
            noAutoDisable: true,
        }] : []),
        ...(canApprove ? [{
            text: denyText,
            action: openDenialModal,
            destructive: true,
            loading: processingAction === 'deny',
            disabled: actionInProgress || savingInvoice,
            noAutoDisable: true,
        }] : []),
    ];

    // A blocking rule reads as a violation, everything else (warnings and rules
    // needing an exception, approved or not) as advisory.
    const blockingPolicyIssues = policyIssues.filter((issue) => issue?.effect === 'block');
    const advisoryPolicyIssues = policyIssues.filter((issue) => issue?.effect !== 'block');

    // Plain render functions, not inline components: rendering these as
    // <InvoiceDetails /> gave React a brand new component type on every
    // render, so the background approval refresh (approvals.js) remounted the
    // whole detail pane and threw the approver out of the field they were
    // typing in, discarding the uncommitted text.
    // Traces the filled pane below, reusing the very components it renders —
    // the InvoiceFormItem grid (with the narrow billable control and the
    // plain-text requester), the totals card behind its divider, and the cost
    // detail block — so the layout cannot drift from the loaded state.
    const renderInvoiceDetailsSkeleton = () => {
        const skeletonField = (key, labelWidth, child) => (
            <InvoiceFormItem
                key={key}
                label={<Skeleton variant="text" width={labelWidth}/>}
                alwaysUseMobileStyling={true}
                labelClassName="text-muted align-self-start"
                data={data}
                show={true}
            >
                {child}
            </InvoiceFormItem>
        );

        const amountPlaceholder = (key, alignEnd) => (
            <div key={key} className={`col-6 ${alignEnd ? 'd-flex flex-column align-items-end' : ''}`}>
                <Skeleton variant="text" width={120} sx={{fontSize: '1.2rem'}}/>
                <Skeleton variant="text" width={160} height={38}/>
            </div>
        );

        return (
            <div
                style={{fontSize: '1.2rem'}}
                aria-busy="true"
                aria-label={translate(data, 'loading', 'Loading')}
            >
                <div className='container-fluid'>
                    <div className={data.isMobile ? "row g-3 row-cols-1" : "row g-3 row-cols-2"}>
                        {/* Five extracted fields, then the billable control sharing
                            row three, leaving the requester alone on row four —
                            the order a filled invoice actually lands in. */}
                        {Array.from({length: 5}).map((_, field) => skeletonField(
                            `invoice-skeleton-field-${field}`,
                            140,
                            <Skeleton variant="rectangular" height={34} sx={{borderRadius: 1}}/>
                        ))}
                        {skeletonField(
                            'invoice-skeleton-billable',
                            90,
                            <Skeleton variant="rectangular" width={22} height={22} sx={{borderRadius: 1}}/>
                        )}
                        {skeletonField(
                            'invoice-skeleton-requester',
                            90,
                            <Skeleton variant="text" width={180} height={30}/>
                        )}
                    </div>
                </div>

                <Divider className={"my-4"}/>

                <div className={"container-fluid border p-4 rounded-sm bg-light"}>
                    <div className="row g-3">
                        {amountPlaceholder('invoice-skeleton-grand-total', false)}
                        {amountPlaceholder('invoice-skeleton-total', true)}
                    </div>
                </div>

                <Skeleton variant="text" width={140} height={28} className="mt-4 mb-2"/>
                <Skeleton variant="rectangular" height={120} sx={{borderRadius: 1}}/>
            </div>
        );
    };

    const renderInvoiceDetails = () => {
        if (loadingInvoice) return renderInvoiceDetailsSkeleton();
        if (invoiceError) {
            return <div
                className="text-center text-danger">{translate(data, 'error_loading_invoice', 'Error loading invoice.')}</div>;
        }
        if (!invoice) {
            return <div
                className="text-center text-muted">{translate(data, 'no_invoice_found', 'No invoice found.')}</div>;
        }

        const displayedSupplier = invoice.json && invoice.json.ListSupplier && invoice.json.ListSupplier.length > 0 ? invoice.json.ListSupplier[0] : {};
        const draftSupplier = draftJson.ListSupplier && draftJson.ListSupplier.length > 0 ? draftJson.ListSupplier[0] : {};
        const supplierName = draftSupplier.Name;
        const supplierNumber = draftSupplier.Number;
        const billingDate = draftJson.InvoiceDate;
        const dueDate = draftJson.DueDate;
        const invoiceNo = draftJson.InvoiceNo;
        const invoiceRef = draftJson.InvoiceRef;
        const currency = draftJson.Currency;
        const total = draftJson.Total ?? '';
        const totalWithTax = draftJson.TotalWithTax ?? '';
        const lineItems = draftJson.ListItem || [];
        const isAderant = Utils.isExportFormat(data, 'aderant');
        const invoiceFields = [
            {
                label: data.t.read('invoice.supplier_name'),
                value: supplierName,
                displayValue: displayedSupplier.Name,
                // The vendor must stay fillable even when nothing was extracted.
                alwaysShow: true,
                onChange: (value) => updateDraftSupplierField('Name', value),
                searchURL: '/suppliers/search/',
                searchParams: data.config?.supplier_group
                    ? {type: invoice?.type, transaction_type: invoice?.transaction_type}
                    : null,
                searchOnFocus: true,
                searchCallback: (supplier, typedText) => supplierChosen(supplier, typedText),
                passClearRecommendationFunc: (fnc) => {
                    clearSupplierRecommendation.current = fnc;
                },
                // XPEN-3267 — no "add it" affordance when the tenant may not
                // create suppliers; the hint says why the vendor is missing.
                hideAddOperation: !Utils.clientAllowsSupplierCreate(data),
                noAddOperationHint: data.t.read('supplier_not_found_creation_disabled'),
            },
            {
                label: data.t.read('invoice.supplier_number'),
                value: supplierNumber,
                displayValue: displayedSupplier.Number,
                alwaysShow: true,
                onChange: (value) => updateDraftSupplierField('Number', value),
            },
            {
                label: data.t.read('invoice.invoice_number'),
                value: invoiceNo,
                displayValue: invoice.json?.InvoiceNo,
                onChange: (value) => updateDraftField('InvoiceNo', value),
            },
            {
                label: data.t.read('invoice.invoice_ref'),
                value: invoiceRef,
                displayValue: invoice.json?.InvoiceRef,
                maxLength: isAderant ? ADERANT_HEADER_REFERENCE_MAXLENGTH : undefined,
                onChange: (value) => updateDraftField('InvoiceRef', value),
            },
            {
                label: data.t.read('invoice.billing_date'),
                value: billingDate,
                displayValue: invoice.json?.InvoiceDate,
                format: 'date',
                onChange: (value) => updateDraftField('InvoiceDate', value),
            },
            {
                label: data.t.read('invoice.pay_date'),
                value: dueDate,
                displayValue: invoice.json?.DueDate,
                format: 'date',
                show: !data.config?.frontend_invoice?.hide_due_date,
                onChange: (value) => updateDraftField('DueDate', value),
            },
            {
                label: data.t.read('invoice.currency'),
                value: currency,
                displayValue: invoice.json?.Currency,
                onChange: (value) => updateDraftField('Currency', value),
            },
        ].filter((field) => field.show !== false && (field.alwaysShow || hasDisplayValue(field.displayValue)));
        const amountFields = [
            {
                label: translate(data, 'invoice.total_with_tax', 'Total with Tax'),
                value: totalWithTax,
                currency,
            },
            {
                label: translate(data, 'invoice.total', 'Total'),
                value: total,
                currency,
                alignEnd: true,
            },
        ].filter((field) => hasDisplayValue(field.value));
        const hasAmountSummary = amountFields.length > 0;

        return (
            <div className={""} style={{fontSize: '1.2rem'}}>
                <div className='container-fluid'>
                    <div className={data.isMobile ? "row g-3 row-cols-1" : "row g-3 row-cols-2"}>
                        {invoiceFields.map((field) => (
                            <InvoiceFormItem
                                key={field.label}
                                label={field.label}
                                alwaysUseMobileStyling={true}
                                labelClassName="text-muted align-self-start"
                                data={data}
                                show={true}
                            >
                                <ApprovalEditableField
                                    value={field.value}
                                    format={field.format}
                                    maxLength={field.maxLength}
                                    locked={!canEditInvoice}
                                    onChange={field.onChange}
                                    ariaLabel={field.label}
                                    searchURL={field.searchURL}
                                    searchParams={field.searchParams}
                                    searchOnFocus={field.searchOnFocus}
                                    searchCallback={field.searchCallback}
                                    passClearRecommendationFunc={field.passClearRecommendationFunc}
                                    hideAddOperation={field.hideAddOperation}
                                    noAddOperationHint={field.noAddOperationHint}
                                />
                            </InvoiceFormItem>
                        ))}
                        <InvoiceFormItem
                            label={translate(data, 'invoice.is_billable', 'Is billable')}
                            alwaysUseMobileStyling={true}
                            labelClassName="text-muted align-self-start"
                            data={data}
                            show={true}
                        >
                            <BillableCheckbox
                                data={data}
                                value={draftJson.IsBillable}
                                disabled={!canEditInvoice}
                                onChange={updateDraftInvoiceBillable}
                            />
                        </InvoiceFormItem>
                        {renderApprovalRequesterField()}
                    </div>

                </div>
                
                {hasAmountSummary && (
                    <>
                        <Divider className={"my-4"} />
                        <div className={"container-fluid border p-4 rounded-sm bg-light"}>
                            <div className="row g-3">
                                {amountFields.map((field) => (
                                    <div key={field.label} className={`col-6 ${field.alignEnd ? 'd-flex flex-column align-items-end' : ''}`}>
                                        <h4 className="text-muted  mb-1">{field.label}</h4>
                                        <div className="d-flex align-items-end">
                                            <h3 className="font-weight-bolder mb-0">{formatMoney(field.value)}</h3>
                                            {hasDisplayValue(field.currency) && <span className="ms-1  text-secondary">{field.currency}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}

                <InvoiceLineItemsTable
                    data={data}
                    invoice={invoice}
                    lineItems={lineItems}
                    locked={!canEditInvoice}
                    onLineItemChange={updateDraftLineItemField}
                    onEditExpense={openExpenseItem}
                />

            </div>
        )
    }

    const renderPolicyIssuesAlert = (issues, danger = false) => {
        if (!issues.length) return null;

        const heading = danger
            ? translate(data, 'approval_modal.expense_policy_violation', 'Expense policy violation')
            : translate(data, 'approval_modal.expense_policy_warning', 'Expense policy warning');

        return (
            <div
                className={`alert ${danger ? 'alert-danger' : 'alert-warning'} mb-3`}
                role="alert"
                data-testid={danger ? 'expense-policy-blocking-alert' : 'expense-policy-warning-alert'}
            >
                <div className="d-flex align-items-center mb-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" title={heading} />
                    <strong>{heading}</strong>
                </div>
                <ul className="mb-0 pl-4">
                    {issues.map((issue, index) => {
                        // Name what the issue is about: the receipt for an expense-scoped
                        // rule, the report itself for an account-scoped one.
                        const sourceLabel = issue.scope === 'expense_account'
                            ? translate(data, 'approval_modal.expense_account_scope', 'Expense account')
                            : issue.expense_name || issue.expense_checksum;
                        const message = translate(data, issue.message, issue.message || '');
                        // An already-approved exception still shows, marked as settled, so
                        // the approver is not left wondering whether it was ever handled.
                        const exceptionApproved = (
                            issue.effect === 'exception_required'
                            && issue.exception_status === 'approved'
                        );

                        return (
                            <li key={`${issue.scope || 'expense'}-${issue.rule_id || issue.message}-${issue.expense_checksum || index}`}>
                                {sourceLabel && <strong className="mr-1">{sourceLabel}:</strong>}
                                <span>{message}</span>
                                {exceptionApproved && (
                                    <span className="badge badge-success ml-2">
                                        {translate(data, 'approval_modal.exception_approved', 'Exception approved')}
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    };

    const renderLineItemDetails = () => {
        if (!lineItem) {
            return (
                <div className="container-fluid border p-4 rounded-sm">
                    <h4 className="mb-3">{translate(data, 'approval_modal.line_item_details', 'Line item details')}</h4>
                    <div className="text-muted">{translate(data, 'approval_modal.no_line_item', 'No line item available')}</div>
                </div>
            );
        }

        // Route against the draft, not the saved invoice: toggling a line's billable
        // flag must swap the coding family straight away.
        const invoiceWithDraft = {...invoice, json: draftJson};
        const fields = buildLineItemFields(data, invoiceWithDraft, lineItem);

        return (
            <div className="container-fluid border p-4 rounded-sm bg-light">
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                        <h4 className="mb-1">{translate(data, 'approval_modal.line_item_details', 'Line item details')}</h4>
                        <div className="text-muted">
                            {translate(data, 'approval_modal.only_relevant_line_item', 'Only the relevant line item is shown for this approval.')}
                        </div>
                    </div>
                    <span className="badge bg-secondary">
                        {translate(data, 'approval_requests_table.line_item', 'Line item')}
                    </span>
                </div>

                <div className="container-fluid">
                    {(displayedLineItem?._mileage_origin || displayedLineItem?._mileage_destination) && (
                        <div className="row g-3 row-cols-2 mb-3">
                            {displayedLineItem._mileage_origin && (
                                <InvoiceFormItem
                                    alwaysUseMobileStyling={true}
                                    labelClassName="text-muted align-self-start"
                                    data={data}
                                    label={translate(data, 'approval_modal.mileage_departure', 'Departure')}
                                    show={true}
                                >
                                    <div className="fw-bold">{displayedLineItem._mileage_origin}</div>
                                </InvoiceFormItem>
                            )}
                            {displayedLineItem._mileage_destination && (
                                <InvoiceFormItem
                                    alwaysUseMobileStyling={true}
                                    labelClassName="text-muted align-self-start"
                                    data={data}
                                    label={translate(data, 'approval_modal.mileage_arrival', 'Arrival')}
                                    show={true}
                                >
                                    <div className="fw-bold">{displayedLineItem._mileage_destination}</div>
                                </InvoiceFormItem>
                            )}
                        </div>
                    )}
                    <div className="row g-3 row-cols-2">
                        <InvoiceFormItem
                            alwaysUseMobileStyling={true}
                            labelClassName="text-muted align-self-start"
                            data={data}
                            label={translate(data, 'approval_requests_table.assigned_approver', 'Assigned approver')}
                            show={true}
                        >
                            <div className="fw-bold">{formatUserName(approvalRequest.assigned_approver) || '-'}</div>
                        </InvoiceFormItem>
                        <InvoiceFormItem
                            alwaysUseMobileStyling={true}
                            labelClassName="text-muted align-self-start"
                            data={data}
                            label={translate(data, 'invoice.is_billable', 'Is billable')}
                            show={true}
                        >
                            <BillableCheckbox
                                data={data}
                                value={lineItem.IsBillable == null ? draftJson.IsBillable : isBillableLineItem(lineItem)}
                                disabled={!canEditInvoice}
                                onChange={(value) => updateDraftSelectedLineItemField('IsBillable', value ? 1 : 0)}
                            />
                        </InvoiceFormItem>
                        {fields.filter((field) => field.key !== 'Status' && isRenderableLineItemValue(displayedLineItem?.[field.key])).map((field) => (
                            <InvoiceFormItem
                                key={field.key}
                                alwaysUseMobileStyling={true}
                                labelClassName="text-muted align-self-start"
                                data={data}
                                label={field.key === 'Total' && invoice?.type === 'XPENSE' ? expenseLineAmountLabel(data) : translate(data, field.labelKey, field.fallback)}
                                show={true}
                            >
                                <ApprovalLineItemInput
                                    data={data}
                                    invoice={invoice}
                                    row={lineItem}
                                    onEditExpense={openExpenseItem}
                                    fieldKey={field.key}
                                    value={lineItem[field.key]}
                                    format={field.format || 'data'}
                                    maxLength={field.maxLength}
                                    locked={!canEditInvoice}
                                    onChange={(value) => updateDraftSelectedLineItemField(field.key, value)}
                                    ariaLabel={field.key === 'Total' && invoice?.type === 'XPENSE' ? expenseLineAmountLabel(data) : translate(data, field.labelKey, field.fallback)}
                                    classes="bg-transparent border-0 px-0 fw-bold"
                                />
                            </InvoiceFormItem>
                        ))}
                        {renderApprovalRequesterField()}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {promptNewSupplier && (
                <SupplierForm
                    supplier={{
                        names: [newSupplierName],
                        TaxCode: resolveDefaultTaxCode(data.config?.tax_profile),
                    }}
                    cancelSupplierModal={() => setPromptNewSupplier(false)}
                    setShowSupplierModal={(show) => setPromptNewSupplier(show)}
                    callerCallback={(supplier) => supplierChosen(supplier, null)}
                    simplifiedForm={!Utils.userHasRole(data, ["SuperAdmin", "Admin", "Manager"])}
                    origin_invoice_id={approvalRequest.invoice_id}
                />
            )}
            {askSupplierCodesAvailable != null && (
                <Modal
                    variant="confirm"
                    title={translate(data, 'confirm', 'Confirm')}
                    cancelAction={() => setAskSupplierCodesAvailable(null)}
                    okAction={confirmSupplierNumber}
                    okText={translate(data, 'save', 'Save')}
                    cancelText={translate(data, 'cancel', 'Cancel')}
                >
                    <p>
                        {`${translate(data, 'invoice.choose_supplier_number_for', 'Choose a supplier number for')} `}
                        <strong>{currentSupplier?.name}</strong>
                    </p>
                    <div>
                        {askSupplierCodesAvailable.map((supplierCode) => (
                            <div key={supplierCode.code}>
                                <label onClick={() => setChosenSupplierNumber(supplierCode.code)}>
                                    <input
                                        type='radio'
                                        name='supplier-number-radio'
                                        checked={chosenSupplierNumber === supplierCode.code}
                                        value={supplierCode.code}
                                        onChange={() => {}}
                                    />
                                    <strong> #{supplierCode.code}</strong> : {supplierCode.desc}
                                </label>
                            </div>
                        ))}
                    </div>
                </Modal>
            )}
            <Modal
                title={title}
                width="92vw"
                height="85vh"
                overflow="hidden"
                cancelAction={onClose}
                cancelText={translate(data, 'close', 'close')}
                isDirty={canEditInvoice && hasUnsavedChanges}
                secondaryActions={secondaryFooterActions}
                okAction={primaryFooterAction?.action}
                okText={primaryFooterAction?.text}
                okLoading={primaryFooterAction?.loading}
                invalid={primaryFooterAction?.disabled}
                noProcessingLoading
            >
                <div className={"invoice invoice-approval-modal bg-white"} style={{height: '100%', minHeight: 0}}>
                    {
                        !data.isMobile &&
                        <PDFVisualizer
                            pdfBuff={pdfUrl}
                            maxWidth={"45vw"}
                            minWidth={"25vw"}
                            data={data}
                            downloadFile={downloadFile}
                            id={approvalRequest.invoice_id}
                            isLoading={loadingPdf}
                            ownerType="invoice"
                            ownerId={approvalRequest.invoice_id}
                            attachmentsCount={attachmentsCount}
                            pdfView={pdfView}
                            changePdfView={setPdfView}
                        />
                    }
                    <div
                        className={data.isMobile ? 'content-section d-flex flex-column mb-5' : 'content-section d-flex flex-column'}
                        style={{minWidth: '35vh', minHeight: 0, height: '100%', overflowX: 'auto'}}
                    >

                        {/* Mobile PDF display*/}
                        {data.isMobile && <PdfImageDisplay file={pdfUrl} isLoading={loadingPdf}/>}
                        {data.isMobile && pdfError && (
                            <div className="alert alert-danger mx-4 mt-3">
                                {translate(data, 'approval_modal.pdf_load_error', 'PDF preview could not be loaded.')}
                            </div>
                        )}

                        <div style={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            height: '100%',
                        }}>

                            <div className='d-flex p-4 flex-column justify-content-between'>
                                {actionError && <div className="alert alert-danger">{actionError}</div>}
                                {saveError && <div className="alert alert-danger">{saveError}</div>}
                                {calculatingTaxes && <div role="status">{translate(data, 'approval_modal.calculating_taxes', 'Calculating taxes…')}</div>}
                                {taxError && <div role="alert" className="alert alert-danger">
                                    {translate(data, 'error_approval_tax_calculation', 'Taxes could not be calculated. Please try again.')}
                                    <button type="button" className="btn btn-link" onClick={() => {
                                        setTaxError(false);
                                        setDraftInvoiceJson((current) => cloneValue(current));
                                    }}>{translate(data, 'approval_modal.retry_taxes', 'Retry')}</button>
                                </div>}
                                {taxesChanged && !taxesBlocked && <div className="alert alert-info" role="status">
                                    {data.config?.tax_1_label || 'Tax 1'}: {formatMoney(draftJson.Tax1)} ·{' '}
                                    {data.config?.tax_2_label || 'Tax 2'}: {formatMoney(draftJson.Tax2)} ·{' '}
                                    {translate(data, 'invoice.total_with_tax', 'Total with Tax')}: {formatMoney(draftJson.TotalWithTax)} {draftJson.Currency}
                                </div>}
                                {resendError && <div className="alert alert-danger">{resendError}</div>}
                                {isResolvedRequest && (
                                    <div className="alert alert-light border mb-3">
                                        <span className="text-muted mr-2">
                                            {translate(data, 'approval_modal.resolved_status', 'Resolved status')}:
                                        </span>
                                        <strong className={resolvedStatusClassName}>{resolvedStatus}</strong>
                                        {denialReason && (
                                            <div className="mt-2">
                                                <span className="text-muted mr-2">
                                                    {translate(data, 'approval_modal.denial_reason', 'Denial reason')}:
                                                </span>
                                                <span>{denialReason}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {invoice?.type === 'XPENSE' && (
                                    <div className="border rounded-sm p-3 mb-3" aria-label={translate(data, 'approval_requests_table.receipt_total', 'Receipt total')}>
                                        <div className="text-muted">{translate(data, 'approval_requests_table.receipt_total', 'Receipt total')}</div>
                                        <ReceiptTotals data={data} receipts={approvalRequest.receipts} />
                                    </div>
                                )}
                                {approvalRequest.type === 'line_item' && sharedReceiptContext && (
                                    <SharedReceiptNotice
                                        label={translate(data, 'approval_modal.shared_receipt_label', 'Shared receipt')}
                                        notice={translate(
                                            data,
                                            'approval_modal.shared_receipt_notice',
                                            'This approval covers {amount} of a {total} receipt shared between multiple '
                                            + 'matters. The other shares are approved separately.'
                                        )
                                            .replace('{amount}', formatMoney(sharedReceiptContext.currentAmount, invoiceJson.Currency))
                                            .replace('{total}', formatMoney(sharedReceiptContext.receiptTotal, invoiceJson.Currency))}
                                    />
                                )}
                                {approvalRequest.type === 'line_item' ? renderLineItemDetails() : renderInvoiceDetails()}
                                {invoice && !loadingInvoice && !invoiceError && (
                                    <div className="mt-4">
                                        <label htmlFor="approval-invoice-comments" className="h4">
                                            {translate(data, 'comments', 'Comments')}
                                        </label>
                                        <textarea
                                            id="approval-invoice-comments"
                                            className={approvalControlClassName(!canEditInvoice, 'form-control')}
                                            rows={4}
                                            value={draftJson.comments ?? ''}
                                            disabled={!canEditInvoice || savingInvoice || actionInProgress || resendingRequest || deletingRequest}
                                            onChange={(event) => updateDraftField('comments', event.target.value)}
                                        />
                                    </div>
                                )}
                                {policyError && (
                                    <div className="alert alert-warning mt-3 mb-0" role="alert" data-testid="expense-policy-load-error">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                                        {translate(
                                            data,
                                            'approval_modal.expense_policy_load_error',
                                            'Expense policy information could not be loaded.'
                                        )}
                                    </div>
                                )}
                                {(blockingPolicyIssues.length > 0 || advisoryPolicyIssues.length > 0) && (
                                    <div className="mt-3">
                                        {renderPolicyIssuesAlert(blockingPolicyIssues, true)}
                                        {renderPolicyIssuesAlert(advisoryPolicyIssues)}
                                    </div>
                                )}
                            </div>


                        </div>


                    </div>
                </div>
            </Modal>
            {showDeleteConfirmation && (
                <Modal
                    variant="confirm"
                    destructive
                    title={translate(data, 'approval_modal.delete_confirmation_title', 'Confirm deletion')}
                    subtitle={deletesAssociatedExpense
                            ? translate(
                                data,
                                'approval_modal.delete_expense_line_item_confirmation',
                                'Delete this line item and its associated expense? This will also remove every invoice line from that expense.'
                            )
                            : approvalRequest.type === 'line_item'
                                ? translate(
                                    data,
                                    'approval_modal.delete_line_item_confirmation',
                                    'Delete this line item? This action cannot be undone.'
                                )
                                : translate(
                                    data,
                                    'approval_modal.delete_invoice_confirmation',
                                    'Delete this invoice? This action cannot be undone.'
                                )}
                    cancelAction={() => setShowDeleteConfirmation(false)}
                    cancelText={translate(data, 'cancel', 'Cancel')}
                    okAction={confirmDeleteRequest}
                    okText={translate(data, 'delete', 'Delete')}
                    okLoading={deletingRequest}
                    invalid={deletingRequest}
                    noProcessingLoading
                >
                    {deleteError && <div className="alert alert-danger">{deleteError}</div>}
                </Modal>
            )}
            {expenseItem && <Modal
                title={translate(data, 'expense_details', 'Expense Details')}
                size="md"
                isDirty={JSON.stringify(expenseItem) !== pristineExpenseItem.current}
                cancelText={translate(data, 'close', 'Close')}
                cancelAction={() => setExpenseItem(null)}
                okText={translate(data, 'save', 'Save')}
                okAction={applyExpenseItem}
            >
                <ExpenseItemFields data={data} item={expenseItem} onChange={setExpenseItem}
                    taxCodes={data.config?.tax_profile?.map(profile => profile.code) || []}
                    invoiceTaxCode={draftJson.TaxCode}
                    defaultTaxCode={resolveDefaultTaxCode(data.config?.tax_profile)} />
            </Modal>}
            {showDenialModal && (
                <Modal
                    variant="confirm"
                    destructive
                    overflow="visible"
                    title={translate(data, 'approval_modal.denial_title', 'Denial reason')}
                    subtitle={translate(
                        data,
                        'approval_modal.denial_subtitle',
                        'The requester sees this reason.'
                    )}
                    isDirty={causeDenial.trim() !== ''}
                    cancelAction={() => setShowDenialModal(false)}
                    okAction={confirmDenial}
                    okText={translate(data, 'submit', 'Submit')}
                    cancelText={translate(data, 'cancel', 'Cancel')}
                >
                    <input
                        className="form-control"
                        type="text"
                        autoFocus
                        aria-label={translate(data, 'approval_modal.denial_title', 'Denial reason')}
                        value={causeDenial}
                        onChange={(e) => setCauseDenial(e.target.value)}
                    />
                    {denialError && <div className="text-danger mt-2">{denialError}</div>}
                </Modal>
            )}
            {showMoveExpenseModal && (
                <DeniedExpenseMoveModal
                    data={data}
                    requestId={approvalRequest.id}
                    onCancel={() => setShowMoveExpenseModal(false)}
                    onMoved={finishExpenseMove}
                />
            )}
        </>
    );
}

export default inject('data')(observer(InvoiceApprovalModal));
